import { useAuth } from '@/app/_auth/auth-hooks'
import type { ContactFormData, ContactFormType } from '@/app/settings/_components/contact-form'
import { usersApiPaths } from '@/hooks/api/_paths'
import type { EmergenceUser } from '@/hooks/api/use-emergence-user-and-step-query'
import { useApiRequest } from '@/lib/http-utils'
import emailjs from '@emailjs/browser'
import { useMutation, UseMutationOptions } from '@tanstack/react-query'

export function useSaveContactMutation(
  options?: UseMutationOptions<void, unknown, ContactFormData>,
) {
  const { user } = useAuth()
  const { apiRequest } = useApiRequest()
  return useMutation({
    mutationKey: ['contact-form'],
    mutationFn: async (data: ContactFormData) => {
      if (!user) throw new Error('USER_NOT_FOUND')

      const saveResponse = await apiRequest({
        path: usersApiPaths.contact,
        options: {
          method: 'POST',
          body: JSON.stringify(port(data)),
        },
      })

      if (saveResponse.status !== 200) {
        throw new Error('CONTACT_FORM_SAVE_FAILED')
      }

      const emailResponse = await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_CONTACT_TEMPLATE_ID,
        portEmailJS({ data, user }),
        { publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY },
      )

      if (emailResponse.status !== 200) {
        throw new Error('CONTACT_FORM_EMAIL_SEND_FAILED')
      }
    },
    ...options,
  })
}

// 👇 data adapters

type ContactFormAPIRequest = {
  contact_type: ContactFormType
  comment: string
  rating?: string
}

const port = ({ rating, ...rest }: ContactFormData): ContactFormAPIRequest => ({
  ...rest,
  ...(rest.contact_type === 'FEEDBACK' && { rating }),
})

type EmailJSTemplate = {
  email: string
  full_name?: string
  uid: string
} & ContactFormAPIRequest

const portEmailJS = ({
  user,
  data,
}: {
  data: ContactFormData
  user: EmergenceUser
}): EmailJSTemplate => ({
  email: user.email,
  full_name: user.fullName,
  uid: user.uid,
  ...data,
  ...(data.rating && { rating: `${data.rating}/5` }),
})
